import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  auth,
  db,
  storage,
  functions,
  onAuthStateChanged,
  firebaseSignOut,
} from '../services/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile, signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { startTokenRefresh, stopTokenRefresh } from '../utils/tokenRefresh';
import { signUpWithEmail, signInWithEmail, signInWithGooglePopup, resetPasswordEmail, sendEmailSignInLink, signInAsAnonymous, linkAnonymousToGoogle, linkAnonymousToEmailPassword } from '../services/firebaseAuth';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const prevUserRef = useRef(null);

  const fetchUserProfile = useCallback(async (uid) => {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const profile = { id: snap.id, ...snap.data() };
      setUserProfile(profile);
      return profile;
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const wasSignedIn = prevUserRef.current !== null;
      prevUserRef.current = user;

      setCurrentUser(user);
      if (user) {
        setSessionExpired(false);
        await fetchUserProfile(user.uid);
        startTokenRefresh();
      } else {
        stopTokenRefresh();
        if (wasSignedIn) {
          setSessionExpired(true);
        } else {
          setUserProfile(null);
        }
      }
      setLoading(false);
    });
    return () => {
      unsubscribe();
      stopTokenRefresh();
    };
  }, [fetchUserProfile]);

  // Check for SSO token from DoMoreWorld via postMessage (secure)
  useEffect(() => {
    let readySignalSent = false;
    let tokenProcessed = false;
    
    const handleMessage = async (event) => {
      // Security: Only accept messages from DoMoreWorld domains
      const allowedOrigins = [
        'https://valyss-88dcc.web.app',
        'https://app.domoreworld.com',
        'https://domoreworld.com',
        'https://www.domoreworld.com',
        'http://localhost:5173',
        'http://localhost:3000',
        'http://localhost:5174'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }
      
      if (event.data?.type === 'DMW_AUTH_TOKEN' && event.data?.token && !tokenProcessed) {
        tokenProcessed = true; // Prevent processing same token twice
        const dmwToken = event.data.token;
        console.log('[ArtPingPong] SSO token received from DoMoreWorld via postMessage');
        
        try {
          const { signInWithCustomToken } = await import('firebase/auth');
          const { doc, getDoc, setDoc } = await import('firebase/firestore');
          const { db } = await import('../services/firebase');
          
          const userCredential = await signInWithCustomToken(auth, dmwToken);
          console.log('[ArtPingPong] Successfully signed in via SSO:', userCredential.user.uid);
          
          // Check if user profile exists, create/update it for SSO users
          const userRef = doc(db, 'users', userCredential.user.uid);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            // New SSO user - create profile with onboarding completed
            await setDoc(userRef, {
              email: userCredential.user.email || null,
              displayName: userCredential.user.displayName || 'Art Ping Pong User',
              onboardingCompleted: true,
              createdAt: new Date(),
              circleIds: []
            });
            console.log('[ArtPingPong] Created new profile for SSO user');
          } else {
            // Existing user - ensure onboarding is marked complete
            const userData = userSnap.data();
            if (!userData.onboardingCompleted) {
              await setDoc(userRef, { onboardingCompleted: true }, { merge: true });
              console.log('[ArtPingPong] Marked onboarding complete for existing SSO user');
            }
          }
        } catch (error) {
          console.error('[ArtPingPong] SSO sign-in failed:', error);
          tokenProcessed = false; // Allow retry on error
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Send ready signal to parent window after a short delay (only once)
    const sendReadySignal = () => {
      if (window.parent && window.parent !== window && !readySignalSent) {
        readySignalSent = true;
        console.log('[ArtPingPong] Sending ready signal to DoMoreWorld');
        window.parent.postMessage(
          { type: 'APP_READY' },
          '*'  // Parent will validate our origin
        );
      }
    };
    
    // Send ready signal after mount (with small delay to ensure setup)
    const timer = setTimeout(sendReadySignal, 100);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, []);

  // Auth methods matching DoMoreWorld
  const signup = async (email, password, displayName) => {
    // If currently signed in as anonymous, link the account
    if (currentUser && currentUser.isAnonymous) {
      const result = await linkAnonymousToEmailPassword(email, password, displayName);
      if (result.error) {
        throw new Error(result.error);
      }
      return result.user;
    }
    
    // Otherwise, normal email/password signup
    const result = await signUpWithEmail(email, password, displayName);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const login = async (email, password) => {
    const result = await signInWithEmail(email, password);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const signInWithGoogle = async () => {
    // If currently signed in as anonymous, link the anonymous account
    if (currentUser && currentUser.isAnonymous) {
      const result = await linkAnonymousToGoogle();
      if (result.error) {
        throw new Error(result.error);
      }
      return result.user;
    }
    
    // Otherwise, normal Google sign-in
    const result = await signInWithGooglePopup();
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const resetPassword = async (email) => {
    const result = await resetPasswordEmail(email);
    if (result.error) {
      throw new Error(result.error);
    }
  };

  const sendEmailLink = async (email) => {
    const result = await sendEmailSignInLink(email);
    if (result.error) {
      throw new Error(result.error);
    }
  };

  const signInAnonymous = async () => {
    const result = await signInAsAnonymous();
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const linkAccountToGoogle = async () => {
    const result = await linkAnonymousToGoogle();
    if (result.error) {
      const error = new Error(result.error);
      // Attach error code so UI can handle it specially
      if (result.errorCode) {
        error.code = result.errorCode;
      }
      throw error;
    }
    return result.user;
  };

  const uploadProfilePhoto = async (file) => {
    if (!currentUser) {
      throw new Error('Must be signed in to upload a profile photo');
    }
    const filename = `profile_${currentUser.uid}_${Date.now()}.jpg`;
    const storageRef = ref(storage, `profilePictures/${currentUser.uid}/${filename}`);
    await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
    const photoURL = await getDownloadURL(storageRef);

    await updateProfile(currentUser, { photoURL });
    await setDoc(doc(db, 'users', currentUser.uid), { photoURL }, { merge: true });
    await fetchUserProfile(currentUser.uid);
    return photoURL;
  };

  // Supervised child sessions are indefinite (ID token auto-refreshes).
  // Parent can sign the child out if needed.
  // If session expiry is required in future, check lastPinValidated in supervisedPins
  // and force re-auth if age > N hours on app load.
  const signInSupervisedChild = async (childUid, pin) => {
    console.log('🔑 [ArtPingPong] Signing in supervised child:', childUid);
    
    try {
      const validateChildPin = httpsCallable(functions, 'validateChildPin');
      const result = await validateChildPin({ childUid, pin });
      
      console.log('✅ PIN validated, signing in with custom token');
      
      // Sign in with the custom token
      await signInWithCustomToken(auth, result.data.customToken);
      
      console.log('✅ Supervised child signed in successfully');
      
      return result.data;
    } catch (error) {
      console.error('❌ Error signing in supervised child:', error);
      throw error;
    }
  };

  const signOutUser = async () => {
    prevUserRef.current = null;
    setSessionExpired(false);
    setUserProfile(null);
    await firebaseSignOut(auth);
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    sessionExpired,
    signup,
    login,
    signInWithGoogle,
    signInAnonymous,
    linkAccountToGoogle,
    resetPassword,
    sendEmailLink,
    signOutUser,
    uploadProfilePhoto,
    fetchUserProfile,
    signInSupervisedChild,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
